import { useEffect, useState } from "react";
import { ChevronRight, HandCoins, LineChart } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const sisa = (d) => d.amount - (d.paid || 0);

export function HutangAgenSummary({ onOpenMutasi, refreshKey = 0 }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/debts").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line
  const aktif = list.filter((d) => d.status === "aktif");
  const total = aktif.reduce((s, d) => s + sisa(d), 0);
  const perAgent = Object.values(aktif.reduce((acc, d) => {
    const k = d.user_id;
    acc[k] = acc[k] || { user_id: k, name: d.name || d.email, email: d.email, agent_code: d.agent_code || "", total: 0, items: [] };
    acc[k].total += sisa(d); acc[k].items.push(d);
    return acc;
  }, {})).sort((a, b) => b.total - a.total);
  return (
    <>
      <button type="button" onClick={() => { setOpen(true); setSel(null); load(); }}
        className={`rounded-xl border px-4 py-3 text-left transition-colors hover:bg-amber-500/15 ${total > 0 ? "border-amber-500/40 bg-amber-500/10" : "border-border bg-card"}`} data-testid="hutang-agen-summary">
        <div className="flex items-center gap-3">
          <HandCoins className={`w-6 h-6 ${total > 0 ? "text-amber-400" : "text-muted-foreground"}`} />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Hutang Agen</div>
            <div className={`font-mono font-bold text-xl ${total > 0 ? "text-amber-400" : "text-muted-foreground"}`} data-testid="hutang-agen-total">{rupiah(total)}</div>
          </div>
          <div className="ml-auto text-right text-[11px] text-muted-foreground">{perAgent.length} agen<br />{aktif.length} hutang aktif<ChevronRight className="w-3.5 h-3.5 inline ml-1" /></div>
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="hutang-agen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-amber-400" /> Rincian Hutang Agen</DialogTitle>
            <DialogDescription>Total <b className="font-mono text-amber-400">{rupiah(total)}</b> dari {perAgent.length} agen. Klik agen untuk rincian; kelola lewat tombol Mutasi (Tambah Saldo tipe "Hutang Saldo", kartu Hutang Sekarang +/−).</DialogDescription>
          </DialogHeader>
          {!perAgent.length && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="hutang-agen-empty">Tidak ada agen yang memiliki hutang.</p>}
          <div className="divide-y divide-border rounded-lg border border-border">
            {perAgent.map((a) => (
              <div key={a.user_id} data-testid={`hutang-agen-row-${a.agent_code || a.email}`}>
                <button type="button" onClick={() => setSel(sel === a.user_id ? null : a.user_id)}
                  className={`w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-accent/60 transition-colors ${sel === a.user_id ? "bg-amber-500/10" : ""}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center font-mono text-xs font-bold text-amber-500 shrink-0">{a.agent_code || a.name.slice(0, 2).toUpperCase()}</div>
                    <div className="min-w-0"><div className="text-sm font-medium truncate">{a.name}</div><div className="text-[10px] font-mono text-muted-foreground truncate">{a.email} • {a.items.length} hutang</div></div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono font-bold text-amber-400" data-testid={`hutang-agen-total-${a.agent_code || a.email}`}>{rupiah(a.total)}</span>
                    {onOpenMutasi && <Button size="sm" variant="secondary" className="h-7 px-2" title="Buka mutasi agen" onClick={(e) => { e.stopPropagation(); setOpen(false); onOpenMutasi(a); }} data-testid={`hutang-agen-mutasi-${a.agent_code || a.email}`}><LineChart className="w-3.5 h-3.5" /></Button>}
                  </div>
                </button>
                {sel === a.user_id && (
                  <div className="px-4 pb-3 space-y-1" data-testid={`hutang-agen-detail-${a.agent_code || a.email}`}>
                    {a.items.map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-3 text-xs rounded-md bg-accent/40 px-3 py-1.5">
                        <div className="min-w-0"><div className="truncate">{d.note || (d.kind === "koreksi" ? "koreksi admin" : "saldo hutang")}</div><div className="text-[10px] font-mono text-muted-foreground">{fmtDateTime(d.created_at)} • oleh {d.created_by}{(d.paid || 0) > 0 ? ` • terangsur ${rupiah(d.paid)} dari ${rupiah(d.amount)}` : ""}</div></div>
                        <span className="font-mono font-semibold text-amber-400 shrink-0">{rupiah(sisa(d))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
